<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { Cog6ToothIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { AREA_ICONS } from '@/navigation/navIcons';
import NavUser from '@/components/NavUser.vue';
import CreateMenu from '@/layouts/frame/CreateMenu.vue';
import { visibleAreas } from '@/navigation/areas';
import { areaIdForRoute } from '@/navigation/activeArea';
import { areaEntry } from '@/navigation/areaSpots';
import { useAuthStore } from '@/stores/authStore';
import { isGateOpen } from '@/navigation/gates';
import { shortcutLabel } from '@/utils/shortcutLabel';
import { useNavUser } from '@/composables/useNavUser';

/**
 * The 56px strip on the far left. Each icon is an area of the app. The area you
 * are in is highlighted, and each one opens the last list or page you were on
 * in that area. Settings and the account menu sit at the bottom.
 * Areas the user can use nothing in are left out, so their icons never show.
 */

const route = useRoute();
const authStore = useAuthStore();
const quickSearchShortcut = shortcutLabel('K');


const areas = computed(() => visibleAreas(isGateOpen));
const stripAreas = computed(() => areas.value.filter((area) => area.id !== 'settings'));
// Home sits in the top group, right under Search. The gap comes after it, before
// the areas that hold records: Clients, Players, Music and Reports.
const areaGroups = computed(() => [
    stripAreas.value.filter((area) => area.id === 'home'),
    stripAreas.value.filter((area) => area.id !== 'home'),
]);
const settingsArea = computed(() => areas.value.find((area) => area.id === 'settings') ?? null);
// The search page sits outside every area, so no area icon is lit there.
const activeAreaId = computed(() => (route.name === 'search' ? null : areaIdForRoute(route)));

const user = useNavUser();
</script>

<template>
  <nav aria-label="Areas" class="flex h-full w-14 shrink-0 flex-col items-center gap-1 py-2">
    <CreateMenu />
    <!-- The full search page. Its tooltip names the shortcut for the quick modal. -->
    <router-link
      to="/search"
      aria-label="Search everything"
      :title="`Search (${quickSearchShortcut} for quick search)`"
      data-testid="strip-search"
      :aria-current="route.name === 'search' ? 'page' : undefined"
      class="flex size-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-sidebar-accent hover:text-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-foreground"
    >
      <MagnifyingGlassIcon class="size-5" aria-hidden="true" />
    </router-link>
    <template v-for="(group, groupIndex) in areaGroups" :key="groupIndex">
    <div v-if="groupIndex > 0" class="h-3" />
    <router-link
      v-for="area in group"
      :key="area.id"
      :to="areaEntry(area, authStore.userData?.id)"
      data-testid="area-button"
      :aria-label="area.label"
      :title="area.label"
      :aria-current="activeAreaId === area.id ? 'page' : undefined"
      class="flex size-10 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-foreground"
    >
      <component :is="AREA_ICONS[area.id]" class="size-5" aria-hidden="true" />
      <span class="sr-only">{{ area.label }}</span>
    </router-link>
    </template>
    <div class="flex-1" />
    <router-link
      v-if="settingsArea"
      :to="areaEntry(settingsArea, authStore.userData?.id)"
      data-testid="settings-button"
      aria-label="Settings"
      title="Settings"
      :aria-current="activeAreaId === 'settings' ? 'page' : undefined"
      class="flex size-10 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-foreground"
    >
      <Cog6ToothIcon class="size-5" aria-hidden="true" />
      <span class="sr-only">Settings</span>
    </router-link>
    <NavUser :user="user" compact />
  </nav>
</template>
